import { AppBar, Box, Button, ButtonGroup, Collapse, InputLabel, List, ListItemButton, ListItemText, Tab, Tabs, ThemeProvider, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStateContext } from "../reducers";
import { Menu } from "../utils/Menu";
import { DataFormFieldType, PageContainerType, PageFilterSendType, TabItemType } from "../utils/Types";
import BaseContainer from "./_BaseContainer";
import { PageContainerActionParamType, buttonTheme, drawerWidth, getPathObject, headerHeight, pageTabHeight, pageToolbarHeight } from "../utils/Common";
import {  } from "@mui/icons-material";
import {
    FilterList as FilterListIcon,
    FirstPage as FirstPageIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    LastPage as LastPageIcon,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import GenericPopoverForm from "./GenericPopoverForm";

function PageContainer(props: PageContainerType) {
    const {groupName, subgroupName, code, title, children, toolbars, filter, customFormCommands, pageCount = 1, onSetPageNumber} = props;
    const [mobileTabOpen, setMobileTabOpen] = useState(false);
    const [showFilterSort, setShowFilterSort] = useState(false);
    const [anchorElFilterSort, setAnchorElFilterSort] = useState<any>(null);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const {menuState} = useStateContext();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const defaultFilterObj = useMemo<any>(() => {
        if (!filter?.defaultFilters?.length)
            return {};

        return Object.fromEntries(filter.defaultFilters.map((filter: PageFilterSendType) => [filter.name, filter.value]));
    }, [filter?.defaultFilters]);

    const handleFilterSortClick = useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (showFilterSort)
        {
            setShowFilterSort(false);
            setAnchorElFilterSort(null);
            return;
        }

        setAnchorElFilterSort(event.currentTarget);
        setShowFilterSort(true);
    }, [showFilterSort]);

    useEffect(() => {
        if (!menuState?.showSidebar)
            return;

        if (!isMobile)
            return;

        setShowFilterSort(false);
    }, [menuState?.showSidebar]);

    const tabs = useMemo(() => {
        const result: TabItemType[] = [];

        if (!Menu.filter(m => m.groupName === groupName && m.id === subgroupName && m.children.length).length)
            return result;

        Menu.filter(m => m.groupName === groupName && m.id === subgroupName && m.children.length).forEach(m => {
            m.children.filter(c => !c.hidden).forEach(c => {
                result.push({
                    ...c,
                    path: `${m.path}${c.path}`,
                    selected: code === c.code,
                });
            });
        });

        return result;
    }, [groupName, subgroupName, code]);

    const pathObject: PageContainerActionParamType | null = useMemo(() => {
        return getPathObject(groupName, subgroupName, code);
    }, [groupName, subgroupName, code]);

    // State to manage the selected tab index
    const [selectedTabIndex, setSelectedTabIndex] = useState(tabs.findIndex(t => t.selected));

    const handleTabChange = useCallback((event: React.SyntheticEvent, value: number) => {
        if (!tabs.length) {
            return;
        }

        const currentTab = tabs[value];
        setSelectedTabIndex(value);
        navigate(currentTab.path);
    }, [tabs, navigate]);

    const handleMobileTabChange = useCallback((tab: TabItemType) => {
        // console.log({tab});
        if (!tab)
            return;

        const tabIndex = tabs.indexOf(tab);
        setSelectedTabIndex(tabIndex);
        navigate(tab.path);
    }, [tabs, navigate]);

    const onPageNumberChangrd = useCallback((isReversing: boolean, goToLast: boolean = false) => {
        const newPageNumber = goToLast ? (isReversing ? 1 : pageCount) : (isReversing ? pageNumber - 1 : pageNumber + 1);
        setPageNumber(newPageNumber);

        if (onSetPageNumber)
            onSetPageNumber(newPageNumber);
    }, [[pageNumber, pageCount]]);

    return (
        <>
            <BaseContainer>
            {
                tabs.length > 0 ?
                <AppBar
                    position="fixed"
                    sx={{
                        marginTop: `${headerHeight}px`,
                        boxShadow: 0,
                        width: { md: menuState?.showSidebar ? `calc(100vw - ${drawerWidth}px)` : "100vw" },
                        ml: { md: `${drawerWidth}px` },
                        height: `${pageTabHeight}px`,
                        zIndex: 1200,
                    }}
                    className="bg-grid-normal"
                >
                {
                    isMobile ?
                    <List
                        sx={{ width: '100%', color: "#000", paddingY: "0 !important" }}
                        component="nav"
                        aria-labelledby="nested-list-subheader"
                        className="text-bg-grid-normal"
                    >
                        <ListItemButton
                            onClick={() => setMobileTabOpen(!mobileTabOpen)}
                            sx={{
                                paddingY: "0 !important",
                                height: `${pageTabHeight}px`,
                                maxHeight: `${pageTabHeight}px`
                            }}
                        >
                            <ListItemText primary={tabs[tabs.indexOf(tabs.filter(t => t.selected)[0])].title} />
                            {mobileTabOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </ListItemButton>
                        <Collapse in={mobileTabOpen}>
                            <List component="div" disablePadding sx={{backgroundColor: "#fff"}}>
                            {
                                tabs.map((tab, idx) => {
                                    return (
                                        <ListItemButton
                                            key={`tab_${idx}`}
                                            sx={{
                                                pl: 4,
                                                '&.Mui-selected': {
                                                    backgroundColor: '#FFE4E1',
                                                },
                                            }}
                                            onClick={() => handleMobileTabChange(tab)} selected={idx === selectedTabIndex}
                                        >
                                            <ListItemText primary={tab.title} />
                                        </ListItemButton>
                                    );
                                })
                            }
                            </List>
                        </Collapse>
                    </List>
                    :
                    <Box sx={{ width: "100%", height: `${pageTabHeight}px` }}>
                        <Tabs
                            value={tabs.indexOf(tabs.filter(t => t.selected)[0])}
                            onChange={handleTabChange}
                            aria-label="menu tabs"
                            variant="scrollable"
                            TabIndicatorProps={{
                                className: "bg-color-default",
                            }}
                        >
                        {
                            tabs.map((t, idx) => {
                                return (
                                    <Tab label={t.title} id={`tab_${idx}`} key={`tab_${idx}`} sx={{ textTransform: "none" }} className="text-bg-grid-normal" />
                                );
                            })
                        }
                        </Tabs>
                    </Box>
                }
                </AppBar>
                :
                <></>
            }
                <AppBar
                    position="fixed"
                    sx={{
                        marginTop: `${headerHeight + pageTabHeight}px`,
                        boxShadow: "0 1px 6px 1px rgba(200, 200, 200, 0.7)",
                        width: { md: menuState?.showSidebar ? `calc(100vw - ${drawerWidth}px)` : "100vw" },
                        ml: { md: `${drawerWidth}px` },
                        height: `${pageToolbarHeight}px`,
                        paddingLeft: "1.5rem",
                        paddingRight: "1.5rem",
                        paddingTop: "0.5rem",
                        paddingBottom: "0.5rem",
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "center",
                        alignItems: "center",
                        marginBottom: "0.5rem",
                    }}
                    className="bg-color-page-toolbar"
                >
                    <Box
                        sx={{
                            flexGrow: 1,
                            textAlign: "left",
                            height: `${pageToolbarHeight}px`,
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "start",
                            alignItems: "center",
                        }}
                    >
                    {
                        toolbars?.length ?
                        <ThemeProvider theme={buttonTheme}>
                            <ButtonGroup>
                            {
                                toolbars.map((t, t_idx) => (
                                    <Tooltip
                                        key={`toolbar_${t_idx}`}
                                        title={t.title}
                                        arrow
                                    >
                                        <Button variant={t.variant ?? "contained"} color={t.color ?? "primary"} onClick={() => t.action(pathObject)} sx={{marginLeft: `${t_idx ? "2px" : "0"} !important`}} disabled={t.disabled ?? false}>
                                            <t.icon />
                                        </Button>
                                    </Tooltip>
                                ))
                            }
                            </ButtonGroup>
                        </ThemeProvider>
                        :
                        null
                    }
                    </Box>
                    <Box
                        sx={{
                            gap: 1,
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "end",
                            alignItems: "center",
                            height: `${pageToolbarHeight}px`,
                        }}
                    >
                        <Box
                            sx={{
                                height: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <InputLabel>{pageNumber} | {pageCount}</InputLabel>
                        </Box>
                        <ThemeProvider theme={buttonTheme}>
                            <ButtonGroup
                                sx={{
                                    height: "100%",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                            {
                                !isMobile ?
                                <Tooltip
                                    title="Prev"
                                    arrow
                                >
                                    <Button
                                        variant="outlined"
                                        color="dark"
                                        onClick={() => onPageNumberChangrd(true, true)}
                                        disabled={pageNumber === 1}
                                    >
                                        <FirstPageIcon />
                                    </Button>
                                </Tooltip>
                                :
                                null
                            }
                                <Tooltip
                                    title="Prev"
                                    arrow
                                >
                                    <Button
                                        variant="outlined"
                                        color="dark"
                                        onClick={() => onPageNumberChangrd(true)}
                                        disabled={pageNumber === 1}
                                    >
                                        <ChevronLeftIcon />
                                    </Button>
                                </Tooltip>
                                <Tooltip
                                    title="Filter"
                                    arrow
                                >
                                    <Button
                                        variant="outlined"
                                        color="dark"
                                        onClick={() => onPageNumberChangrd(false)}
                                        disabled={pageNumber === pageCount}
                                    >
                                        <ChevronRightIcon />
                                    </Button>
                                </Tooltip>
                            {
                                !isMobile ?
                                <Tooltip
                                    title="Prev"
                                    arrow
                                >
                                    <Button
                                        variant="outlined"
                                        color="dark"
                                        onClick={() => onPageNumberChangrd(false, true)}
                                        disabled={pageNumber === pageCount}
                                    >
                                        <LastPageIcon />
                                    </Button>
                                </Tooltip>
                                :
                                null
                            }
                            </ButtonGroup>
                        {
                            filter?.fields.length ?
                            <ButtonGroup
                                sx={{
                                    marginLeft: "0.5rem",
                                }}
                            >
                                <Tooltip
                                    title="Filter"
                                    arrow
                                >
                                    <Button variant="outlined" color="dark" onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => handleFilterSortClick(event)}>
                                        <FilterListIcon />
                                    </Button>
                                </Tooltip>
                            </ButtonGroup>
                            :
                            null
                        }
                        </ThemeProvider>
                    </Box>
                </AppBar>
            {
                tabs.length > 0 ?
                <Box
                    sx={{
                        marginTop: `calc(${(isMobile ? headerHeight : 0) + pageTabHeight + pageToolbarHeight}px)`, width: "100%",
                    }}
                    className="bg-color-transparent"
                >
                </Box>
                :
                null
            }
            {
                children
            }
            </BaseContainer>
        {
            showFilterSort ?
            <GenericPopoverForm<any>
                title={`Filter/Pengurutan ${title}`}
                data={defaultFilterObj}
                fields={
                    filter?.fields.map(c => {
                        return {
                            name: c.fieldName,
                            title: c.title,
                            inputType: c.inputType,
                            dataType: c.fieldType,
                            isReadonly: c.readonly ?? false,
                            gridSizeSm: c.gridSizeSm,
                            gridSizeMd: c.gridSizeMd,
                            gridSizeLg: c.gridSizeLg,
                            gridSizeXl: c.gridSizeXl,
                            onChanged: c.onChanged,
                            titleHasCommands: c.canSort && c.titleHasCommands,
                            titleCommands: c.titleCommands,
                        } as DataFormFieldType<any>;
                    }) as DataFormFieldType<any>[]
                }
                customFormCommands={customFormCommands}
                anchorEl={anchorElFilterSort}
            />
            :
            null
        }
        </>
    );
}

export default PageContainer;
